import React, {
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react'

import {
  AccordionContextType,
  AccordionImperativeHandle,
  AccordionProps,
} from './interface'

const AccordionContext = createContext<AccordionContextType>({
  active: false,
})

function withAccordion(Component: React.FC<AccordionProps>) {
  const HOC = forwardRef<AccordionImperativeHandle, AccordionProps>(
    ({ initialActive = false, ...props }, ref) => {
      const [active, setActive] = useState(initialActive)
      const [rotate, setRotate] = useState(
        'transform duration-300 ease rotate-180'
      )
      const contentSpace = useRef<HTMLDivElement>(null)

      useEffect(() => {
        if (initialActive) {
          setRotate('transform duration-300 ease rotate')
        }
      }, [initialActive])

      const toggleAccordion = (event: React.MouseEvent<HTMLElement>) => {
        if (event.target instanceof HTMLInputElement) return

        setActive(active === false)

        setRotate(
          active
            ? 'transform duration-300 ease rotate-180'
            : 'transform duration-300 ease'
        )
      }

      const imperativeToggleAccordion = useCallback((flag: boolean) => {
        setActive(flag)

        setRotate(
          flag
            ? 'transform duration-300 ease rotate-180'
            : 'transform duration-300 ease'
        )
      }, [])

      useImperativeHandle(
        ref,
        () => ({
          active,
          setActive,
          toggleAccordion: imperativeToggleAccordion,
        }),
        [active, setActive, imperativeToggleAccordion]
      )

      const newProps = {
        ...props,
        active,
        rotate,
        contentSpace,
        toggleAccordion,
      }

      return (
        <AccordionContext.Provider value={{ active }}>
          <Component {...newProps} />
        </AccordionContext.Provider>
      )
    }
  )

  return HOC
}

export { withAccordion }

export function useAccordion() {
  return useContext(AccordionContext)
}
